import type { Cell } from './cell';
import { type BaseExcelColor, ExcelColor, TRANSPARENT } from './excelColor';
import {
  DEFAULT_RICH_TEXT_STYLE,
  type RichText,
  type RichTextStyle,
} from './richText';
import type { Table } from './table';
import type { BaseStyle } from './types';
import { Vector2 } from '@/utils/vec';

export type LineStyle = 'solid' | 'dashed' | 'dotted' | 'double';

export type BorderStyle = {
  readonly color: BaseExcelColor;
  readonly width: number;
  readonly style: LineStyle;
};

export type CellJustify = 'left' | 'center' | 'right';
export type CellVerticalAlign = 'top' | 'middle' | 'bottom';

/*
 * TODO: a way to make e.g.
 * One line:
 * FIRST NAME — NOM DE FAMILLE
 *
 * Two lines:
 *   FIRST NAME
 * NOM DE FAMILLE
 *
 * (note the missing hyphen in the two line version)
 */

export type CellStyle = RichTextStyle & {
  justify: CellJustify;
  verticalAlign: CellVerticalAlign;
  textWrap: boolean;
  lineHeight: number;
  rotation: number;
  borderTop: BorderStyle;
  borderLeft: BorderStyle;
  padding: Vector2;
};

export const DEFAULT_CELL_STYLE: CellStyle = {
  ...DEFAULT_RICH_TEXT_STYLE,
  justify: 'left',
  verticalAlign: 'middle',
  textWrap: false,
  lineHeight: 1,
  rotation: 0,
  borderTop: {
    color: ExcelColor.fromHex('#0000002a'),
    width: 1,
    style: 'solid',
  },
  borderLeft: {
    color: ExcelColor.fromHex('#0000002a'),
    width: 1,
    style: 'solid',
  },
  padding: new Vector2(4, 4),
};

export const HIDDEN_CELL_STYLE: CellStyle = {
  ...DEFAULT_CELL_STYLE,
  borderTop: { color: TRANSPARENT, width: 0, style: 'solid' },
  borderLeft: { color: TRANSPARENT, width: 0, style: 'solid' },
};

export interface RenderedCell {
  readonly cell: Cell | undefined;
  readonly table: Table | undefined;
  readonly value: RichText | string;
  /** Cell position, not pixel position */
  readonly pos: Vector2;
  readonly style: Readonly<CellStyle>;
}

export function cellStyleToCss(
  style: BaseStyle<CellStyle>,
): React.CSSProperties {
  const ret: React.CSSProperties = {};

  if (style.background) {
    ret.backgroundColor = style.background.toHex();
  }

  if (style.justify === 'left') {
    ret.textAlign = 'left';
  } else if (style.justify === 'center') {
    ret.textAlign = 'center';
  } else if (style.justify === 'right') {
    ret.textAlign = 'right';
  }

  if (style.verticalAlign === 'top') {
    ret.verticalAlign = 'top';
  } else if (style.verticalAlign === 'middle') {
    ret.verticalAlign = 'middle';
  } else if (style.verticalAlign === 'bottom') {
    ret.verticalAlign = 'bottom';
  }

  if (style.textWrap) {
    ret.whiteSpace = 'normal';
  }

  if (style.lineHeight) {
    ret.lineHeight = style.lineHeight;
  }

  if (style.padding) {
    ret.padding = `${style.padding.y}px ${style.padding.x}px`;
  }

  return ret;
}
